import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useTrialStatus } from '../hooks/useTrialStatus';
import { useMarkPageVisited } from '../hooks/usePageVisit';
import TrialExpiredOverlay from '../components/TrialExpiredOverlay';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { 
  Search, 
  Plus, 
  Minus, 
  ShoppingCart, 
  Package,
  LayoutGrid,
  List,
  Filter,
  Truck,
  SlidersHorizontal,
  X
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const CatalogPage = () => {
  const { api } = useAuth();
  const { addToCart, cart, updateQuantity, getCartCount, getCartTotal } = useCart();
  const { isTrialExpired } = useTrialStatus();
  const navigate = useNavigate();
  
  // Mark this page as visited
  useMarkPageVisited('/catalog');
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('list'); // Default to list view
  const [selectedRayon, setSelectedRayon] = useState('all');
  const [selectedSupplier, setSelectedSupplier] = useState('all');
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Generate consistent color for supplier based on name hash
  const getSupplierColor = (supplierName) => {
    const colors = [
      { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
      { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
      { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
      { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
      { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200' },
      { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200' },
      { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
      { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
      { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200' },
      { bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200' },
    ];
    
    // Simple hash based on supplier name
    let hash = 0;
    const safeName = supplierName || '';
    for (let i = 0; i < safeName.length; i++) {
      hash = ((hash << 5) - hash) + safeName.charCodeAt(i);
      hash = hash & hash;
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await api.get('/products');
        setProducts(response.data);
      } catch (error) {
        console.error('Failed to fetch products:', error);
        toast.error('Erreur lors du chargement des produits');
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [api]);

  // Extract unique rayons from available products
  const availableRayons = useMemo(() => {
    const rayons = products
      .filter(p => p.is_available && p.rayon)
      .map(p => p.rayon);
    return [...new Set(rayons)].sort();
  }, [products]);

  // Extract unique suppliers from available products
  const availableSuppliers = useMemo(() => {
    const suppliers = products
      .filter(p => p.is_available && p.supplier_name)
      .map(p => ({ id: p.supplier_id, name: p.supplier_name }));
    const uniqueMap = new Map(suppliers.map(s => [s.id, s]));
    return Array.from(uniqueMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [products]);

  const filteredProducts = products.filter(product => {
    if (!product.is_available) return false;
    
    const matchesSearch = product.article.toLowerCase().includes(search.toLowerCase()) ||
      product.code.toLowerCase().includes(search.toLowerCase());
    
    const matchesRayon = selectedRayon === 'all' || product.rayon === selectedRayon;
    const matchesSupplier = selectedSupplier === 'all' || product.supplier_id === selectedSupplier;
    
    return matchesSearch && matchesRayon && matchesSupplier;
  });

  const getProductQuantity = (productId) => {
    return cart[productId]?.quantity || 0;
  };

  const handleAddToCart = (product) => {
    addToCart(product);
    toast.success(`${product.article} ajouté au panier`);
  };

  const activeFiltersCount = (selectedRayon !== 'all' ? 1 : 0) + (selectedSupplier !== 'all' ? 1 : 0);

  const clearFilters = () => {
    setSelectedRayon('all');
    setSelectedSupplier('all');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B4332]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 animate-slide-in relative" data-testid="catalog-page">
      {isTrialExpired && <TrialExpiredOverlay />}
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Catalogue</h1>
            <p className="text-sm md:text-base text-gray-500 mt-0.5">
              {filteredProducts.length} articles • {availableSuppliers.length} fournisseurs
            </p>
            <p className="text-sm text-gray-600 mt-2 max-w-2xl">
              Parcourez le catalogue, ajoutez des produits à votre panier et passez commande auprès de vos fournisseurs.
            </p>
          </div>

          {/* Desktop cart button */}
          <div className="hidden md:flex items-center gap-2">
            {getCartCount() > 0 && (
              <Button 
                onClick={() => navigate('/cart')}
                className="bg-[#1B4332] hover:bg-[#0D2E1F] text-white"
                data-testid="go-to-cart-btn"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Panier ({getCartCount()}) - {getCartTotal().toFixed(2)} €
              </Button>
            )}
          </div>
        </div>

        {/* Search and Filters Row */}
        <div className="flex gap-2">
          {/* Search - Takes most space */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-400" />
            <Input
              placeholder="Rechercher..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 md:pl-10 h-11 md:h-12 text-base"
              data-testid="catalog-search-input"
            />
          </div>

          {/* Mobile Filter Button */}
          <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
            <SheetTrigger asChild>
              <Button 
                variant="outline" 
                className="md:hidden h-11 px-3 relative"
                data-testid="mobile-filter-btn"
              >
                <SlidersHorizontal className="w-5 h-5" />
                {activeFiltersCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#1B4332] text-white text-xs rounded-full flex items-center justify-center">
                    {activeFiltersCount}
                  </span>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="h-auto max-h-[80vh] rounded-t-3xl">
              <SheetHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <SheetTitle className="text-lg">Filtres</SheetTitle>
                  {activeFiltersCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={clearFilters} className="text-red-600">
                      <X className="w-4 h-4 mr-1" />
                      Effacer
                    </Button>
                  )}
                </div>
              </SheetHeader>
              <div className="space-y-4 pb-6">
                {/* Supplier filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Fournisseur</label>
                  <Select value={selectedSupplier} onValueChange={(v) => { setSelectedSupplier(v); }}>
                    <SelectTrigger className="h-12">
                      <Truck className="w-4 h-4 mr-2 text-gray-400" />
                      <SelectValue placeholder="Tous les fournisseurs" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les fournisseurs</SelectItem>
                      {availableSuppliers.map((supplier) => (
                        <SelectItem key={supplier.id} value={supplier.id}>
                          {supplier.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Rayon filter */}
                {availableRayons.length > 0 && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Rayon</label>
                    <Select value={selectedRayon} onValueChange={(v) => { setSelectedRayon(v); }}>
                      <SelectTrigger className="h-12">
                        <Filter className="w-4 h-4 mr-2 text-gray-400" />
                        <SelectValue placeholder="Tous les rayons" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous les rayons</SelectItem>
                        {availableRayons.map((rayon) => (
                          <SelectItem key={rayon} value={rayon}>{rayon}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <Button 
                  className="w-full h-12 bg-[#1B4332] hover:bg-[#0D2E1F] mt-4"
                  onClick={() => setFiltersOpen(false)}
                >
                  Voir {filteredProducts.length} résultats
                </Button>
              </div>
            </SheetContent>
          </Sheet>

          {/* Desktop Filters */}
          <div className="hidden md:flex items-center gap-2">
            {availableSuppliers.length > 1 && (
              <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
                <SelectTrigger className="w-[180px] h-12" data-testid="supplier-filter-select">
                  <Truck className="w-4 h-4 mr-2 text-gray-400" />
                  <SelectValue placeholder="Fournisseur" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  {availableSuppliers.map((supplier) => (
                    <SelectItem key={supplier.id} value={supplier.id}>{supplier.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {availableRayons.length > 0 && (
              <Select value={selectedRayon} onValueChange={setSelectedRayon}>
                <SelectTrigger className="w-[160px] h-12" data-testid="rayon-filter-select">
                  <Filter className="w-4 h-4 mr-2 text-gray-400" />
                  <SelectValue placeholder="Rayon" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  {availableRayons.map((rayon) => (
                    <SelectItem key={rayon} value={rayon}>{rayon}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* View mode toggle */}
            <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className={viewMode === 'grid' ? 'bg-white shadow-sm' : ''}
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className={viewMode === 'list' ? 'bg-white shadow-sm' : ''}
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Active filters chips - Mobile */}
        {activeFiltersCount > 0 && (
          <div className="flex flex-wrap gap-2 md:hidden">
            {selectedSupplier !== 'all' && (
              <Badge 
                variant="secondary" 
                className="bg-[#D8F3DC] text-[#1B4332] px-3 py-1.5 flex items-center gap-1"
              >
                <Truck className="w-3 h-3" />
                {availableSuppliers.find(s => s.id === selectedSupplier)?.name}
                <button onClick={() => setSelectedSupplier('all')} className="ml-1">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
            {selectedRayon !== 'all' && (
              <Badge 
                variant="secondary" 
                className="bg-[#D8F3DC] text-[#1B4332] px-3 py-1.5 flex items-center gap-1"
              >
                <Filter className="w-3 h-3" />
                {selectedRayon}
                <button onClick={() => setSelectedRayon('all')} className="ml-1">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Mobile Grid View (optimized for touch) */}
      <div className="md:hidden">
        <div className="grid grid-cols-1 gap-3">
          {filteredProducts.map((product) => {
            const quantity = getProductQuantity(product.id);
            
            return (
              <Card 
                key={product.id}
                className={`border transition-all ${quantity > 0 ? 'border-[#1B4332] bg-[#D8F3DC]/10' : 'border-gray-200'}`}
                data-testid={`product-card-${product.code}`}
              >
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    {/* Product Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-[10px] font-mono px-1.5 py-0">
                          {product.code}
                        </Badge>
                        {(() => {
                          const sc = getSupplierColor(product.supplier_name);
                          return (
                            <Badge className={`text-[10px] ${sc.bg} ${sc.text} ${sc.border} px-1.5 py-0`}>
                              {product.supplier_name}
                            </Badge>
                          );
                        })()}
                      </div>
                      
                      <h3 className="font-semibold text-gray-900 text-sm leading-tight mb-1 line-clamp-2">
                        {product.article}
                      </h3>
                      
                      {/* Affichage conditionnement */}
                      {product.supplier_packaging_quantity && product.is_divisible !== undefined ? (
                        <div className="text-xs text-gray-600 space-y-0.5 mt-1">
                          <p className="text-gray-500">
                            Vendu: {product.supplier_packaging_quantity}×{product.supplier_packaging_unit} par {product.supplier_packaging_type}
                          </p>
                          {product.is_divisible ? (
                            <p className="font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded inline-block">
                              ✓ Détaillable par {product.divisible_quantity || 1} {product.divisible_unit}
                            </p>
                          ) : (
                            <p className="text-gray-500">Non détaillable</p>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500">{product.min_packaging} • {product.unit}</p>
                      )}
                    </div>

                    {/* Price and Actions */}
                    <div className="flex flex-col items-end gap-2">
                      <div className="text-right">
                        <span className="text-lg font-bold text-[#1B4332] tabular-nums whitespace-nowrap">
                          {(product.catalog_price || product.unit_price || 0).toFixed(2)} €
                        </span>
                        {product.catalog_unit ? (
                          <p className="text-xs text-gray-500">/ {product.catalog_unit}</p>
                        ) : product.price_description ? (
                          <p className="text-xs text-gray-500">{product.price_description}</p>
                        ) : (
                          <p className="text-xs text-gray-500">/ {product.order_unit || product.unit}</p>
                        )}
                      </div>
                      
                      {quantity === 0 ? (
                        <Button 
                          size="sm"
                          onClick={() => handleAddToCart(product)}
                          className="bg-[#1B4332] hover:bg-[#0D2E1F] text-white h-10 w-10 p-0"
                          data-testid={`add-to-cart-${product.code}-btn`}
                        >
                          <Plus className="w-5 h-5" />
                        </Button>
                      ) : (
                        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                          <Button 
                            size="icon" 
                            variant="ghost"
                            className="h-9 w-9 hover:bg-white"
                            onClick={() => updateQuantity(product.id, quantity - 1)}
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <span className="w-8 text-center font-bold text-[#1B4332] tabular-nums">
                            {quantity}
                          </span>
                          <Button 
                            size="icon" 
                            variant="ghost"
                            className="h-9 w-9 hover:bg-white"
                            onClick={() => updateQuantity(product.id, quantity + 1)}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Desktop/Tablet List View */}
      {viewMode === 'list' && (
        <Card className="border border-gray-200 hidden md:block overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold w-[100px]">Code</TableHead>
                <TableHead className="font-semibold">Article</TableHead>
                <TableHead className="font-semibold w-[140px]">Fournisseur</TableHead>
                <TableHead className="font-semibold w-[100px]">Rayon</TableHead>
                <TableHead className="font-semibold w-[180px]">Conditionnement</TableHead>
                <TableHead className="font-semibold text-right w-[140px]">Prix</TableHead>
                <TableHead className="font-semibold text-center w-[160px]">Quantité</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProducts.map((product) => {
                const quantity = getProductQuantity(product.id);
                return (
                  <TableRow 
                    key={product.id} 
                    className={quantity > 0 ? 'bg-[#D8F3DC]/30' : ''}
                    data-testid={`product-row-${product.code}`}
                  >
                    <TableCell className="font-mono text-xs">{product.code}</TableCell>
                    <TableCell className="font-medium">{product.article}</TableCell>
                    <TableCell>
                      {(() => {
                        const sc = getSupplierColor(product.supplier_name);
                        return (
                          <Badge variant="outline" className={`text-xs ${sc.bg} ${sc.text} ${sc.border}`}>
                            {product.supplier_name}
                          </Badge>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-gray-500 text-sm">{product.rayon || '-'}</TableCell>
                    <TableCell className="text-gray-600 text-sm">
                      {/* Nouveau système : affichage complet du conditionnement */}
                      {product.supplier_packaging_quantity && product.is_divisible !== undefined ? (
                        <div className="space-y-0.5">
                          <p className="text-xs text-gray-500">
                            Vendu: {product.supplier_packaging_quantity}×{product.supplier_packaging_unit} par {product.supplier_packaging_type}
                          </p>
                          {product.is_divisible ? (
                            <p className="text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded inline-block">
                              ✓ Détaillable par {product.divisible_quantity || 1} {product.divisible_unit}
                            </p>
                          ) : (
                            <p className="text-xs text-gray-500">Non détaillable</p>
                          )}
                        </div>
                      ) : product.order_unit ? (
                        <span className="font-medium">{product.order_unit}</span>
                      ) : (
                        <>{product.min_packaging} {product.unit}</>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="text-right">
                        <span className="font-semibold text-[#1B4332] tabular-nums">
                          {(product.catalog_price || product.unit_price || 0).toFixed(2)} €
                        </span>
                        {product.catalog_unit ? (
                          <p className="text-xs text-gray-500 mt-0.5">/ {product.catalog_unit}</p>
                        ) : product.price_description ? (
                          <p className="text-xs text-gray-500 mt-0.5">{product.price_description}</p>
                        ) : (
                          <p className="text-xs text-gray-500 mt-0.5">/ {product.order_unit || product.unit}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-1">
                        {quantity === 0 ? (
                          <Button 
                            size="sm"
                            onClick={() => handleAddToCart(product)}
                            className="bg-[#1B4332] hover:bg-[#0D2E1F] text-white h-9 px-4"
                            data-testid={`add-to-cart-${product.code}-btn`}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Ajouter
                          </Button>
                        ) : (
                          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                            <Button 
                              size="icon" 
                              variant="ghost"
                              className="h-8 w-8 hover:bg-white"
                              onClick={() => updateQuantity(product.id, quantity - 1)}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <span className="w-10 text-center font-semibold tabular-nums">
                              {quantity}
                            </span>
                            <Button 
                              size="icon" 
                              variant="ghost"
                              className="h-8 w-8 hover:bg-white"
                              onClick={() => updateQuantity(product.id, quantity + 1)}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Desktop/Tablet Grid View */}
      {viewMode === 'grid' && (
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredProducts.map((product) => {
            const quantity = getProductQuantity(product.id);
            
            return (
              <Card 
                key={product.id}
                className={`border transition-all hover:shadow-md ${quantity > 0 ? 'border-[#1B4332] bg-[#D8F3DC]/10' : 'border-gray-200'}`}
                data-testid={`product-card-${product.code}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <Badge variant="outline" className="text-xs font-mono">
                      {product.code}
                    </Badge>
                    {(() => {
                      const sc = getSupplierColor(product.supplier_name);
                      return (
                        <Badge className={`text-xs ${sc.bg} ${sc.text} ${sc.border}`}>
                          {product.supplier_name}
                        </Badge>
                      );
                    })()}
                  </div>

                  <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2 min-h-[2.5rem]">
                    {product.article}
                  </h3>
                  
                  {/* Affichage conditionnement */}
                  {product.supplier_packaging_quantity && product.is_divisible !== undefined ? (
                    <div className="text-xs text-gray-600 space-y-1 mb-2">
                      <p className="text-gray-500">
                        Vendu: {product.supplier_packaging_quantity}×{product.supplier_packaging_unit} par {product.supplier_packaging_type}
                      </p>
                      {product.is_divisible ? (
                        <p className="font-medium text-green-700 bg-green-50 px-2 py-1 rounded inline-block">
                          ✓ Détaillable par {product.divisible_quantity || 1} {product.divisible_unit}
                        </p>
                      ) : (
                        <p className="text-gray-500">Non détaillable</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 mb-1">{product.min_packaging} • {product.unit}</p>
                  )}
                  
                  {product.rayon && (
                    <Badge variant="outline" className="text-xs mb-3 bg-[#D8F3DC] text-[#1B4332]">
                      {product.rayon}
                    </Badge>
                  )}

                  <div className="flex items-center justify-between mt-3">
                    <div>
                      <span className="text-lg font-bold text-[#1B4332] tabular-nums">
                        {(product.catalog_price || product.unit_price || 0).toFixed(2)} €
                      </span>
                      {product.catalog_unit ? (
                        <span className="text-xs text-gray-500">/{product.catalog_unit}</span>
                      ) : (
                        <span className="text-xs text-gray-500">/{product.unit}</span>
                      )}
                    </div>

                    {quantity === 0 ? (
                      <Button 
                        size="sm"
                        onClick={() => handleAddToCart(product)}
                        className="bg-[#1B4332] hover:bg-[#0D2E1F] text-white"
                        data-testid={`add-to-cart-${product.code}-btn`}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    ) : (
                      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                        <Button 
                          size="icon" 
                          variant="ghost"
                          className="h-8 w-8 hover:bg-white"
                          onClick={() => updateQuantity(product.id, quantity - 1)}
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <span className="w-8 text-center font-semibold tabular-nums">
                          {quantity}
                        </span>
                        <Button 
                          size="icon" 
                          variant="ghost"
                          className="h-8 w-8 hover:bg-white"
                          onClick={() => updateQuantity(product.id, quantity + 1)}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {filteredProducts.length === 0 && (
        <div className="text-center py-12">
          <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">Aucun produit trouvé</h3>
          <p className="text-gray-500">Essayez une autre recherche ou modifiez les filtres</p>
        </div>
      )}

      {/* Floating Cart Button */}
      {getCartCount() > 0 && (
        <div className="fixed bottom-20 lg:bottom-6 left-4 right-4 lg:left-1/2 lg:-translate-x-1/2 lg:w-auto z-40">
          <Button 
            onClick={() => navigate('/cart')}
            className="w-full lg:w-auto bg-[#1B4332] hover:bg-[#0D2E1F] text-white shadow-xl shadow-[#1B4332]/30 h-14 lg:h-12 text-base lg:px-8 rounded-2xl"
            data-testid="floating-cart-btn"
          >
            <ShoppingCart className="w-5 h-5 mr-2" />
            <span className="font-semibold">Voir le panier</span>
            <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-lg text-sm">
              {getCartCount()} • {getCartTotal().toFixed(2)} €
            </span>
          </Button>
        </div>
      )}
    </div>
  );
};

export default CatalogPage;
